// Clientes: conta, chave e escopo.
//
// É a tela que faltava para o gate da fase 3 — "um ArcGIS Pro real adiciona a
// camada **com a credencial emitida pelo back office**". Antes disto a chave
// saía por SQL na mão.
//
// Duas decisões visíveis aqui, e as duas são de segurança:
//
// 1. **A chave aparece uma vez.** Depois do fechamento do aviso ela não existe
//    mais em lugar nenhum além do cliente — o banco guarda o hash. A tela diz
//    isso antes, não depois.
// 2. **Não há chave sem coleção.** Escopo é negação por padrão (princípio 3):
//    uma chave vazia não leria nada e pareceria quebrada.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import {
  alterar,
  enviar,
  obter,
  remover,
  type ChaveEmitida,
  type ChaveResumo,
  type Colecao,
  type Conta,
  type Projeto,
} from "../api.js";
import { Carregando, Falha, quando, Vazio } from "../pecas/basicos.js";

export function Clientes({
  avisar,
}: {
  avisar: (texto: string, erro?: boolean) => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [aberta, setAberta] = useState<string | null>(null);
  const [nova, setNova] = useState("");

  const contas = useQuery({ queryKey: ["contas"], queryFn: () => obter<Conta[]>("/contas") });

  // SUSPENDER, não apagar: `core.registros_uso` referencia a conta, e a trilha
  // de acesso é LGPD. Suspender é imediato no gateway sem tocar nas chaves — a
  // autenticação confere o status a cada requisição.
  const alternar = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      alterar<Conta>(`/contas/${id}`, { status }),
    onSuccess: (c) => {
      avisar(
        c.status === "suspensa"
          ? `"${c.nome}" suspensa: as chaves param de ler agora, e nada foi revogado.`
          : `"${c.nome}" reativada.`,
      );
      void cliente.invalidateQueries({ queryKey: ["contas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const criar = useMutation({
    mutationFn: (nome: string) => enviar<{ id: string }>("/contas", { nome }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["contas"] });
      setNova("");
      avisar("Conta criada. Ela ainda não tem credencial.");
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  if (contas.isLoading) return <Carregando />;
  if (contas.error) return <Falha erro={contas.error} />;
  const lista = contas.data ?? [];

  return (
    <div className="rolagem">
      <div className="filtros">
        <input
          aria-label="Nome do cliente"
          placeholder="Nome do cliente"
          value={nova}
          onChange={(e) => setNova(e.target.value)}
        />
        <button
          className="btn"
          disabled={nova.trim() === "" || criar.isPending}
          onClick={() => criar.mutate(nova.trim())}
        >
          Criar conta
        </button>
      </div>

      {lista.length === 0 ? (
        <Vazio>
          Nenhum cliente. Uma conta nasce sem credencial — a chave se emite depois, já com as
          coleções que ela pode ler.
        </Vazio>
      ) : (
        <table className="tabela">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Situação</th>
              <th>Chaves ativas</th>
              <th>Limite diário</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lista.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.nome}
                  <div className="discreto mono">{quando(c.criado_em)}</div>
                </td>
                <td>
                  <span className={c.status === "ativa" ? "pilula pilula-publicado" : "pilula"}>
                    {c.status}
                  </span>
                </td>
                <td className="mono">{c.chaves_ativas}</td>
                <td className="mono discreto">
                  {c.limite_requisicoes_dia ?? "sem limite"}
                  {/* Um ArcGIS Online gasta ~100 requisições só para desenhar
                      uma camada uma vez (medido). Limite baixo demais parece
                      falha do produto, não proteção. */}
                </td>
                <td>
                  <div className="linha-botoes">
                    <button className="btn btn-pequeno" onClick={() => setAberta(c.id)}>
                      Credenciais…
                    </button>
                    <button
                      className={`btn btn-pequeno ${c.status === "ativa" ? "btn-perigo" : ""}`}
                      disabled={alternar.isPending}
                      onClick={() =>
                        alternar.mutate({
                          id: c.id,
                          status: c.status === "ativa" ? "suspensa" : "ativa",
                        })
                      }
                    >
                      {c.status === "ativa" ? "Suspender" : "Reativar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {aberta ? (
        <Credenciais
          conta={lista.find((c) => c.id === aberta)!}
          avisar={avisar}
          aoFechar={() => setAberta(null)}
        />
      ) : null}
    </div>
  );
}

function Credenciais({
  conta,
  avisar,
  aoFechar,
}: {
  conta: Conta;
  avisar: (texto: string, erro?: boolean) => void;
  aoFechar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const [emitida, setEmitida] = useState<ChaveEmitida | null>(null);
  const [projetoId, setProjetoId] = useState("");
  const [projetoNovo, setProjetoNovo] = useState("");

  const chaves = useQuery({
    queryKey: ["chaves", conta.id],
    queryFn: () => obter<ChaveResumo[]>(`/contas/${conta.id}/chaves`),
  });
  const colecoes = useQuery({
    queryKey: ["colecoes"],
    queryFn: () => obter<Colecao[]>("/colecoes"),
  });
  const projetos = useQuery({
    queryKey: ["projetos", conta.id],
    queryFn: () => obter<Projeto[]>(`/contas/${conta.id}/projetos`),
  });

  const criarProjeto = useMutation({
    mutationFn: (nome: string) => enviar<Projeto>(`/contas/${conta.id}/projetos`, { nome }),
    onSuccess: () => {
      setProjetoNovo("");
      void cliente.invalidateQueries({ queryKey: ["projetos", conta.id] });
      avisar("Projeto criado.");
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const emitir = useMutation({
    mutationFn: () =>
      enviar<ChaveEmitida>(`/contas/${conta.id}/chaves`, {
        colecoes: escolhidas,
        // O projeto é a OUTRA forma de chegar ao escopo, e ele é expandido no
        // servidor: coleção nova no projeto depois disto não entra sozinha
        // (ADR-020). Por isso a resposta devolve as coleções, e é ela que se
        // mostra abaixo — o pedido não diz o que foi concedido.
        projeto_id: projetoId || null,
        // A modalidade é DERIVADA do que se marcou, e confirmada antes de
        // emitir. O ADR-006 quer escopo mínimo porque a chave vaza no item
        // compartilhado do AGOL; o ADR-011 prevê o cliente nacional com 27
        // coleções. Deixar o escopo largo virar padrão por descuido é o que a
        // confirmação impede — marcar três caixas não pode trocar o modelo de
        // risco em silêncio.
        // Com projeto, a contagem do cliente não basta para decidir a
        // modalidade: quem sabe quantas coleções ele tem é a listagem.
        modalidade:
          escolhidas.length + (projetos.data?.find((p) => p.id === projetoId)?.colecoes ?? 0) > 1
            ? "conjunto"
            : "camada",
      }),
    onSuccess: (r) => {
      setEmitida(r);
      setEscolhidas([]);
      setProjetoId("");
      void cliente.invalidateQueries({ queryKey: ["chaves", conta.id] });
      void cliente.invalidateQueries({ queryKey: ["contas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const revogar = useMutation({
    mutationFn: (id: string) => remover(`/chaves/${id}`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["chaves", conta.id] });
      void cliente.invalidateQueries({ queryKey: ["contas"] });
      avisar("Chave revogada. Ela não é apagada: a trilha de acesso é LGPD.");
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  // Só coleção COM versão vigente: uma sem versão não serve feição nenhuma, e
  // dar escopo sobre ela entrega uma credencial que não funciona.
  const publicaveis = (colecoes.data ?? []).filter((c) => c.versao_vigente !== null);

  return (
    <div className="gaveta-inv">
      <button className="gaveta-fundo" aria-label="Fechar" onClick={aoFechar} />
      <aside className="gaveta" role="dialog" aria-modal="true" aria-label={conta.nome}>
        <div className="gaveta-topo">
          <div className="gaveta-titulo">
            <div>
              <h2>{conta.nome}</h2>
              <div className="mono terciario">credenciais e escopo</div>
            </div>
            <button className="btn btn-pequeno" onClick={aoFechar}>
              Esc
            </button>
          </div>
        </div>

        <div className="gaveta-corpo">
          {emitida ? (
            <div className="aviso chave-nova" role="alert">
              <strong>Copie agora. Esta chave não aparece de novo.</strong>
              <p className="discreto">
                O banco guarda apenas o hash. Se ela se perder, emita outra e revogue esta — é o
                único desenho em que “vazou a chave” tem resposta.
              </p>
              <code className="mono chave-valor">{emitida.chave}</code>
              <p className="discreto">
                Escopo concedido: <b>{emitida.colecoes.join(", ")}</b>
                {emitida.projeto_id !== null ? " (expandido do projeto)" : ""}
              </p>
              <div className="linha-botoes">
                <button
                  className="btn"
                  onClick={() => {
                    void navigator.clipboard?.writeText(emitida.chave);
                    avisar("Chave copiada.");
                  }}
                >
                  Copiar
                </button>
                <button className="btn btn-pequeno" onClick={() => setEmitida(null)}>
                  Já copiei
                </button>
              </div>
            </div>
          ) : null}

          <h3>Projetos</h3>
          <p className="nota-principio">
            O projeto agrupa as camadas privadas deste cliente e vira <b>unidade de escopo</b>: uma
            chave pode ser emitida sobre ele inteiro. Existe para conter o estrago de uma chave que
            circulou — sem ele, a escolha é entre uma chave por coleção e uma que lê tudo do cliente
            (ADR-020).
          </p>
          {conta.slug === null ? (
            <div className="aviso" role="status">
              <b>Esta conta não tem slug</b>
              Coleção privada precisa do prefixo da conta no `id_publico` — é o que impede dois
              clientes de disputarem o mesmo nome, e o `id_publico` é contrato: o analista o salva
              no projeto de ArcGIS dele. Sem slug, o banco recusa criar a coleção.
            </div>
          ) : null}
          {(projetos.data ?? []).length === 0 && !projetos.isLoading ? (
            <p className="discreto">Nenhum projeto.</p>
          ) : (
            <ul className="lista-escopo">
              {(projetos.data ?? []).map((p) => (
                <li key={p.id}>
                  <code className="mono">{p.nome}</code>
                  <span className="discreto">
                    {" "}
                    {p.camadas} camada{p.camadas === 1 ? "" : "s"} · {p.colecoes} coleç
                    {p.colecoes === 1 ? "ão" : "ões"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="linha-botoes">
            <input
              aria-label="Nome do projeto"
              placeholder="Nome do projeto"
              value={projetoNovo}
              onChange={(e) => setProjetoNovo(e.target.value)}
            />
            <button
              className="btn"
              disabled={projetoNovo.trim() === "" || criarProjeto.isPending}
              onClick={() => criarProjeto.mutate(projetoNovo.trim())}
            >
              {criarProjeto.isPending ? "Criando…" : "Criar projeto"}
            </button>
          </div>

          <h3>Emitir credencial</h3>
          {publicaveis.length === 0 ? (
            <p className="nota-principio">
              Nenhuma coleção publicada. Uma chave precisa de ao menos uma coleção: sem escopo ela
              não lê nada.
            </p>
          ) : (
            <>
              <p className="nota-principio">
                A chave nasce com escopo (ADR-006). Marque o que este cliente pode ler — coleção
                fora da lista é ausente para ele, não negada.
              </p>
              {(projetos.data ?? []).length > 0 ? (
                <div className="campo">
                  <label htmlFor="proj-escopo">Ou o projeto inteiro</label>
                  <select
                    id="proj-escopo"
                    value={projetoId}
                    onChange={(e) => setProjetoId(e.target.value)}
                  >
                    <option value="">Nenhum</option>
                    {(projetos.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome} ({p.colecoes} coleç{p.colecoes === 1 ? "ão" : "ões"})
                      </option>
                    ))}
                  </select>
                  <p className="campo-ajuda">
                    As coleções são <b>expandidas agora</b>, não resolvidas a cada requisição:
                    coleção nova no projeto depois disto <b>não entra</b> nesta chave. É deliberado
                    — a credencial vaza no item compartilhado do AGOL, e assim o estrago fica fixo
                    no instante da emissão.
                  </p>
                </div>
              ) : null}
              <ul className="lista-escopo">
                {publicaveis.map((c) => (
                  <li key={c.id_publico}>
                    <label>
                      <input
                        type="checkbox"
                        checked={escolhidas.includes(c.id_publico)}
                        onChange={(e) =>
                          setEscolhidas((atual) =>
                            e.target.checked
                              ? [...atual, c.id_publico]
                              : atual.filter((x) => x !== c.id_publico),
                          )
                        }
                      />
                      <code className="mono">{c.id_publico}</code>
                      <span className="discreto"> {c.titulo}</span>
                    </label>
                  </li>
                ))}
              </ul>
              {escolhidas.length > 1 ? (
                <div className="aviso" role="status">
                  <b>Esta chave vai virar um CONJUNTO, não um token de camada</b>
                  Uma chave por coleção é o padrão porque a credencial{" "}
                  <b>vaza no item compartilhado</b> do ArcGIS Online — medido. Com{" "}
                  {escolhidas.length} coleções, quem receber o item recebe as {escolhidas.length}. É
                  o cliente nacional do ADR-011, e é uma decisão, não um detalhe.
                </div>
              ) : null}
              <div className="linha-botoes">
                <button
                  className="btn btn-primario"
                  disabled={(escolhidas.length === 0 && projetoId === "") || emitir.isPending}
                  onClick={() => {
                    if (
                      escolhidas.length > 1 &&
                      !window.confirm(
                        `Emitir UMA chave para ${String(escolhidas.length)} coleções?\n\n` +
                          "A credencial vaza no item compartilhado do ArcGIS Online. " +
                          "Se vazar, entrega todas.\n\n" +
                          "Cancele para emitir uma chave por coleção, que é o padrão.",
                      )
                    ) {
                      return;
                    }
                    emitir.mutate();
                  }}
                >
                  {emitir.isPending ? "Emitindo…" : "Emitir chave"}
                </button>
              </div>
            </>
          )}

          <h3>Chaves desta conta</h3>
          {chaves.isLoading ? <Carregando /> : null}
          {(chaves.data ?? []).length === 0 && !chaves.isLoading ? (
            <p className="discreto">Nenhuma chave emitida.</p>
          ) : null}
          <table className="tabela">
            <tbody>
              {(chaves.data ?? []).map((k) => (
                <tr key={k.id}>
                  <td>
                    <code className="mono">{k.prefixo}…</code>
                    <div className="discreto">{k.colecoes.join(", ") || "sem escopo"}</div>
                  </td>
                  <td className="discreto mono">{quando(k.emitida_em)}</td>
                  <td>
                    {k.status === "ativa" ? (
                      <button
                        className="btn btn-pequeno btn-perigo"
                        onClick={() => revogar.mutate(k.id)}
                      >
                        Revogar
                      </button>
                    ) : (
                      <span className="pilula">revogada</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </aside>
    </div>
  );
}
